import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Telescope, Building2, Banknote, Newspaper, Target, UserRound,
  MessageCircleQuestion, Lightbulb, Link2, Loader2, Lock, Sparkles, RefreshCw, Info,
  ShieldAlert, ListChecks, Download, PhoneCall,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-brand-primary" />
      <h3 className="text-sm font-semibold text-zinc-800">{title}</h3>
    </div>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
    <div className="text-sm text-zinc-700">{value || 'Not publicly available'}</div>
  </div>
);

function ResearchDetail({ research }) {
  const p = research.profile || {};
  const snap = p.company_snapshot || {};
  const fin = p.financials || null;
  const contact = p.contact_profile || {};
  const customMetrics = p.custom_metrics || [];
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/research/${research.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `research_${(research.prospect_company || 'prospect').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download the PDF');
    } finally {
      setDownloading(false);
    }
  };

  const callLink = `/?company=${encodeURIComponent(research.prospect_company || '')}${research.website_url ? `&website=${encodeURIComponent(research.website_url)}` : ''}`;

  return (
    <div className="space-y-4" data-testid="research-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-zinc-900" data-testid="research-detail-company">{research.prospect_company}</h2>
          <p className="text-xs text-zinc-500">
            {research.prospect_domain} · researched {formatDate(research.created_at)} by {research.created_by_name}
            {research.from_cache && <span className="ml-2 text-emerald-600 font-medium">reused from cache — no credit used</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} className="rounded-lg" data-testid="research-download-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Download PDF
          </Button>
          <Button asChild size="sm" className="rounded-lg bg-brand-primary hover:bg-brand-hover">
            <Link to={callLink} data-testid="research-proceed-call-btn">
              <PhoneCall className="w-4 h-4 mr-1.5" />
              Proceed to call
            </Link>
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-400 -mt-2" data-testid="research-call-attach-note">
        "Proceed to call" opens the call setup with this prospect filled in — the research then rides along automatically, visible in the in-call panel and woven into your live coaching.
      </p>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex gap-2.5" data-testid="research-verify-disclaimer">
        <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <strong>Verify before you rely on it.</strong> This research is AI-generated from a limited set of public sources and is indicative only — AI can make errors or miss recent changes. Double-check key facts (financials, names, roles, news) before your call.
        </p>
      </div>

      <Section icon={Building2} title="Company Snapshot" testId="research-snapshot">
        <p className="text-sm text-zinc-700 mb-3">{snap.summary}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KV label="Industry" value={snap.industry} />
          <KV label="Company size" value={snap.company_size} />
          <KV label="Headquarters" value={snap.headquarters} />
        </div>
      </Section>

      {customMetrics.length > 0 && (
        <Section icon={ListChecks} title="Your Research Metrics" testId="research-custom-metrics">
          <div className="space-y-3">
            {customMetrics.map((m, i) => (
              <div key={i} className="bg-zinc-50 rounded-xl p-3" data-testid={`custom-metric-result-${i}`}>
                <div className="text-xs uppercase tracking-wide text-zinc-400 mb-1">{m.metric}</div>
                <div className="text-sm text-zinc-700">{m.finding || 'Not publicly available'}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">Metrics defined by your organisation's custom research template.</p>
        </Section>
      )}

      {fin && (
        <Section icon={Banknote} title="Financials & Funding" testId="research-financials">
          <div className="space-y-2">
            <KV label="Financial signals" value={fin.summary} />
            <KV label="Funding" value={fin.funding} />
          </div>
        </Section>
      )}

      {(p.recent_developments || []).length > 0 && (
        <Section icon={Newspaper} title="Recent News & Changes" testId="research-news">
          <div className="space-y-3">
            {p.recent_developments.map((d, i) => (
              <div key={i} className="border-l-2 border-brand-primary/30 pl-3">
                <div className="text-sm font-medium text-zinc-800">{d.headline} {d.date && <span className="text-xs text-zinc-400 font-normal">· {d.date}</span>}</div>
                <div className="text-xs text-zinc-500">{d.why_it_matters}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(p.challenges_and_fit || []).length > 0 && (
        <Section icon={Target} title="Their Challenges & How You Fit" testId="research-fit">
          <div className="space-y-3">
            {p.challenges_and_fit.map((c, i) => (
              <div key={i} className="bg-zinc-50 rounded-xl p-3">
                <div className="text-sm font-medium text-zinc-800">{c.challenge}</div>
                <div className="text-sm text-emerald-700 mt-1"><span className="font-semibold">Your angle:</span> {c.our_angle}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section icon={UserRound} title="Who You're Meeting" testId="research-contact">
        {contact.name || research.contact_name ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-zinc-800">{contact.name || research.contact_name} {(contact.title || research.contact_title) && <span className="text-zinc-500 font-normal">— {contact.title || research.contact_title}</span>}</div>
            <KV label="Tenure" value={contact.tenure_note} />
            <KV label="Background" value={contact.background} />
          </div>
        ) : (
          <p className="text-sm text-zinc-500">No meeting contact was provided for this research.</p>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={MessageCircleQuestion} title="Discovery Questions" testId="research-questions">
          <ul className="space-y-2">
            {(p.discovery_questions || []).map((q, i) => (
              <li key={i} className="text-sm text-zinc-700 flex gap-2"><span className="text-brand-primary font-semibold">{i + 1}.</span>{q}</li>
            ))}
          </ul>
        </Section>
        <Section icon={Lightbulb} title="Talking Points" testId="research-talking-points">
          <ul className="space-y-2">
            {(p.talking_points || []).map((t, i) => (
              <li key={i} className="text-sm text-zinc-700 flex gap-2"><Sparkles className="w-3.5 h-3.5 mt-0.5 text-brand-primary shrink-0" />{t}</li>
            ))}
          </ul>
        </Section>
      </div>

      <Section icon={Link2} title="Sources" testId="research-sources">
        <ul className="space-y-1">
          {(research.sources || []).map((s, i) => (
            <li key={i} className="text-xs text-zinc-500 break-all">{s}</li>
          ))}
        </ul>
        {p.confidence_note && <p className="text-xs text-zinc-400 mt-3 italic">{p.confidence_note}</p>}
        <p className="text-[11px] text-zinc-400 mt-2">PipeGuide draws from a maximum of 5 public sources per research. AI-generated content can contain errors — treat all findings as indicative and verify critical facts before the call.</p>
      </Section>
    </div>
  );
}

export default function ProspectResearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ website_url: '', company_name: '', contact_name: '', contact_title: '' });

  const loadAll = async () => {
    try {
      const [s, l] = await Promise.all([axios.get(`${API}/research/status`), axios.get(`${API}/research/list`)]);
      setStatus(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      console.error('research load failed', e);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Prefill / deep-link from the pre-call setup screen shortcut
  useEffect(() => {
    const openId = searchParams.get('open');
    const company = searchParams.get('company');
    const website = searchParams.get('website');
    if (openId) {
      openResearch(openId);
      setSearchParams({}, { replace: true });
    } else if (company || website) {
      setForm((f) => ({ ...f, company_name: company || f.company_name, website_url: website || f.website_url }));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addonEnabled = status?.addon_enabled;
  const outOfCredits = status && status.remaining <= 0;
  const locked = !addonEnabled || outOfCredits;

  const toggleAddon = async (enabled) => {
    try {
      await axios.post(`${API}/research/addon`, { enabled });
      toast.success(enabled ? 'Prospect Research is now visible to your team' : 'Prospect Research hidden for your organisation');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const runResearch = async (e) => {
    e.preventDefault();
    if (!form.website_url.trim() || !form.company_name.trim()) {
      toast.error('Company website and name are required');
      return;
    }
    setRunning(true);
    try {
      const res = await axios.post(`${API}/research`, {
        website_url: form.website_url.trim(),
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim() || null,
        contact_title: form.contact_title.trim() || null,
      });
      setSelected(res.data);
      toast.success(res.data.from_cache ? 'Found a recent research — reused for free' : 'Research complete');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Research failed');
    } finally {
      setRunning(false);
    }
  };

  const openResearch = async (id) => {
    try {
      const res = await axios.get(`${API}/research/${id}`);
      setSelected(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error('Could not load research');
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F7F8]">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-zinc-500 hover:text-zinc-800 flex items-center gap-1 text-sm" data-testid="research-back-link">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <span className="text-zinc-300">/</span>
            <div className="flex items-center gap-2 font-semibold text-zinc-900">
              <Telescope className="w-4 h-4 text-brand-primary" /> Prospect Research
            </div>
          </div>
          {status && addonEnabled && (
            <Badge variant="outline" className={`${outOfCredits ? 'border-red-200 text-red-600 bg-red-50' : 'border-emerald-200 text-emerald-700 bg-emerald-50'}`} data-testid="research-quota-badge">
              {status.remaining} of {status.quota} researches left this month
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {status && !addonEnabled && !status.is_admin ? (
          <div className="lg:col-span-12">
            <div className="bg-white border border-dashed border-zinc-300 rounded-2xl p-12 text-center" data-testid="research-hidden-state">
              <Lock className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-zinc-700 mb-1">Prospect Research isn't enabled</h3>
              <p className="text-sm text-zinc-500 max-w-md mx-auto">This feature is turned off for your organisation.</p>
            </div>
          </div>
        ) : (
        <>
        <div className="lg:col-span-4 space-y-4">
          {/* Hidden-feature state (admins only reach this) */}
          {status && !addonEnabled && (
            <Card className="border-zinc-200 rounded-2xl" data-testid="research-addon-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-zinc-400" />
                  <h3 className="text-sm font-semibold text-zinc-800">Research is hidden</h3>
                </div>
                <p className="text-sm text-zinc-600 mb-3">
                  Prospect Research is included in your plan — every meeting token includes one pre-call research ({status.quota}/month per user). It's currently hidden for your organisation.
                </p>
                <Button onClick={() => toggleAddon(true)} className="w-full rounded-xl bg-brand-primary hover:bg-brand-hover" data-testid="research-enable-addon-btn">
                  Show Prospect Research
                </Button>
              </CardContent>
            </Card>
          )}

          {/* New research form — visible always, greyed when locked */}
          <Card className={`border-zinc-200 rounded-2xl ${locked ? 'opacity-60' : ''}`} data-testid="research-form-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-zinc-800 mb-1">Research a prospect</h3>
              <p className="text-xs text-zinc-500 mb-4">Run this before your call — the profile also feeds your live call coaching.</p>
              {outOfCredits && addonEnabled && (
                <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2.5" data-testid="research-out-of-credits">
                  You've used all {status.quota} researches this month — one per meeting token in your monthly allowance. They reset on the 1st. Repeat lookups of already-researched companies are still free.
                </div>
              )}
              <form onSubmit={runResearch} className="space-y-3">
                <Input placeholder="Prospect website (e.g. acme.co.za)" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} disabled={locked || running} className="h-10 rounded-lg" data-testid="research-url-input" />
                <Input placeholder="Company name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} disabled={locked || running} className="h-10 rounded-lg" data-testid="research-company-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Contact name (optional)" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} disabled={locked || running} className="h-10 rounded-lg" data-testid="research-contact-input" />
                  <Input placeholder="Their title (optional)" value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })} disabled={locked || running} className="h-10 rounded-lg" data-testid="research-title-input" />
                </div>
                <Button type="submit" disabled={locked || running} className="w-full h-11 rounded-xl bg-brand-primary hover:bg-brand-hover" data-testid="research-run-btn">
                  {running ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Researching (30-60s)...</>) : (<><Telescope className="w-4 h-4 mr-2" /> Run research</>)}
                </Button>
              </form>
              <div className="mt-4 flex gap-2 text-[11px] text-zinc-400 leading-relaxed" data-testid="research-sources-disclaimer">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Included in your meeting tokens — you get <strong>1 research per meeting token</strong> in your monthly allowance ({status?.quota || ''}/month on your plan). PipeGuide draws from up to <strong>5 public sources</strong>: the prospect's website (up to 3 pages), recent news coverage, and public web mentions. Researches are shared with your team and reused free for {status?.cache_days || 14} days.{status?.template_mode === 'custom' && <> Your organisation uses a <strong>custom template</strong> with {status.template_metric_count} research metric{status.template_metric_count === 1 ? '' : 's'}.</>}</span>
              </div>
              <div className="mt-2 flex gap-2 text-[11px] text-amber-600/90 leading-relaxed" data-testid="research-ai-caution">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Research is AI-generated and indicative only — AI can make errors. Always verify key facts before your call.</span>
              </div>
            </CardContent>
          </Card>

          {/* Past researches */}
          <Card className="border-zinc-200 rounded-2xl" data-testid="research-list-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-zinc-800">Team research library</h3>
                <button onClick={loadAll} className="text-zinc-400 hover:text-zinc-600" data-testid="research-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-zinc-400">No researches yet.</p>
              ) : (
                <div className="space-y-1">
                  {items.map((it) => (
                    <button key={it.id} onClick={() => openResearch(it.id)} className={`w-full text-left px-3 py-2.5 rounded-xl hover:bg-zinc-50 transition-colors ${selected?.id === it.id ? 'bg-zinc-100' : ''}`} data-testid={`research-item-${it.id}`}>
                      <div className="text-sm font-medium text-zinc-800">{it.prospect_company}</div>
                      <div className="text-xs text-zinc-400">
                        {it.contact_name ? `${it.contact_name} · ` : ''}{formatDate(it.created_at)} · {it.created_by_name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {status?.is_admin && addonEnabled && (
            <button onClick={() => toggleAddon(false)} className="text-xs text-zinc-400 hover:text-zinc-600 underline px-1" data-testid="research-disable-addon-btn">
              Hide Prospect Research for your organisation
            </button>
          )}
        </div>

        <div className="lg:col-span-8">
          {selected ? (
            <ResearchDetail research={selected} />
          ) : (
            <div className="bg-white border border-dashed border-zinc-300 rounded-2xl p-12 text-center" data-testid="research-empty-state">
              <Telescope className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-zinc-700 mb-1">Prep before you dial</h3>
              <p className="text-sm text-zinc-500 max-w-md mx-auto">
                Run a research on your prospect and PipeGuide builds a business profile — company snapshot, financial signals, recent news, the challenges you solve for them, and who you're meeting. It then bleeds into your live call coaching automatically.
              </p>
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
